/**
 * Catálogos y reglas de presentación de la gestión de cobranza (perfil del cliente).
 * Los códigos (`result` = tipo de gestión, `channel`, `statusCategory`) los entrega el
 * backend ya normalizados; aquí solo se definen sus etiquetas, colores y el agrupado del historial.
 * La tabla de normalización texto→código está en README.md → "Gestión de cobranza".
 */

/** Nivel de resultado de la gestión (columna `accion`), de menor a mayor avance. */
export const RESULTS = {
  INTENTO: { label: "Intento", tone: "gray", rank: 1 },
  CONTACTO: { label: "Contacto", tone: "blue", rank: 2 },
  EFECTIVO: { label: "Efectivo", tone: "purple", rank: 3 },
  ACUERDO_PAGO: { label: "Acuerdo de pago", tone: "green", rank: 4 },
  OTRO: { label: "Sin clasificar", tone: "gray", rank: 0 },
};

export const CHANNELS = {
  LLAMADA_IA: { label: "Llamada IA" },
  LLAMADA_ASESOR: { label: "Llamada asesor" },
  WHATSAPP: { label: "WhatsApp" },
  OTRO: { label: "Otro canal" },
};

export const STATUS_CATEGORIES = {
  SIN_CONTACTO: "Sin contacto",
  ERROR_TECNICO: "Error técnico",
  CUELGA: "Cuelga o rechaza",
  TERCERO: "Contesta un tercero",
  PROMESA_PAGO: "Promesa de pago",
  PAGO_REPORTADO: "Pago reportado",
  SITUACION_CLIENTE: "Negativa o situación del cliente",
  INTERRUMPIDA: "Llamada interrumpida",
  OTRO: "Sin clasificar",
};

/**
 * Estados conocidos (columna `estado`) y su categoría. Es la misma tabla que usa backend en
 * CollectionManagementNormalizer: si se agrega un estado, agregarlo en ambos.
 */
export const STATUS_CATALOG = [
  { raw: "TIMBRA", category: "SIN_CONTACTO", label: "Timbra sin respuesta" },
  { raw: "Sin respuesta", category: "SIN_CONTACTO", label: "Sin respuesta" },
  { raw: "Buzón de voz", category: "SIN_CONTACTO", label: "Buzón de voz" },
  { raw: "APAGADO", category: "SIN_CONTACTO", label: "Teléfono apagado" },
  { raw: "Línea ocupada", category: "SIN_CONTACTO", label: "Línea ocupada" },
  { raw: "FUERA DE SERVICIO", category: "SIN_CONTACTO", label: "Número fuera de servicio" },
  { raw: "invalid_destination", category: "ERROR_TECNICO", label: "Número inválido" },
  { raw: "telephony_provider_permission_denied", category: "ERROR_TECNICO", label: "Proveedor de telefonía sin permiso" },
  { raw: "telephony_provider_unavailable", category: "ERROR_TECNICO", label: "Proveedor de telefonía no disponible" },
  { raw: "Error Retell", category: "ERROR_TECNICO", label: "Error de la plataforma de llamadas" },
  { raw: "CUELGA", category: "CUELGA", label: "El cliente cuelga" },
  { raw: "Usuario colgó", category: "CUELGA", label: "El cliente colgó" },
  { raw: "user_declined", category: "CUELGA", label: "Rechazó la llamada" },
  { raw: "CUELGA CUANDO SE HABLA DE PRAMI", category: "CUELGA", label: "Cuelga cuando se menciona Prami" },
  { raw: "DEJA DE HABLAR CUANDO HABLA DE PRAMI", category: "CUELGA", label: "Deja de hablar cuando se menciona Prami" },
  { raw: "CONTESTA UN CONOCIDO", category: "TERCERO", label: "Contesta un conocido" },
  { raw: "CONTESTA OTRA PERSONA", category: "TERCERO", label: "Contesta otra persona" },
  { raw: "CRÉDITO A NOMBRE DE UN TERCERO", category: "TERCERO", label: "Crédito a nombre de un tercero" },
  { raw: "DICE QUE REALIZA EL PAGO HOY", category: "PROMESA_PAGO", label: "Dice que paga hoy" },
  { raw: "< 1 SEMANA", category: "PROMESA_PAGO", label: "Promete pagar en menos de 1 semana" },
  { raw: "1 - 2 SEMANAS", category: "PROMESA_PAGO", label: "Promete pagar en 1 a 2 semanas" },
  { raw: "2 - 3 SEMANAS", category: "PROMESA_PAGO", label: "Promete pagar en 2 a 3 semanas" },
  { raw: "COMPROMETIDO", category: "PROMESA_PAGO", label: "Comprometido a pagar" },
  { raw: "MANDA COMPROBANTE", category: "PAGO_REPORTADO", label: "Envía comprobante de pago" },
  { raw: "VA BUSCAR COMPROBANTE", category: "PAGO_REPORTADO", label: "Va a buscar el comprobante" },
  { raw: "PAGANDO", category: "PAGO_REPORTADO", label: "Está pagando" },
  { raw: "RENUENTE", category: "SITUACION_CLIENTE", label: "Renuente a pagar" },
  { raw: "SITUACIÓN ECONÓMICA", category: "SITUACION_CLIENTE", label: "Situación económica" },
  { raw: "CALAMIDAD FAMILIAR", category: "SITUACION_CLIENTE", label: "Calamidad familiar" },
  { raw: "ENFERMEDAD", category: "SITUACION_CLIENTE", label: "Enfermedad" },
  { raw: "DESEMPLEO", category: "SITUACION_CLIENTE", label: "Desempleo" },
  { raw: "INCUMPLIDO", category: "SITUACION_CLIENTE", label: "Incumplió un compromiso" },
  { raw: "Agente colgó", category: "INTERRUMPIDA", label: "El agente terminó la llamada" },
  { raw: "Inactividad", category: "INTERRUMPIDA", label: "Sin interacción en la llamada" },
  { raw: "Duración máxima", category: "INTERRUMPIDA", label: "Alcanzó la duración máxima" },
  { raw: "NO HABLAN", category: "INTERRUMPIDA", label: "No hablan en la llamada" },
  { raw: "DICE QUE NO ESCUCHA", category: "INTERRUMPIDA", label: "Dice que no escucha" },
];

export const statusKey = (raw) => String(raw ?? "").trim().toUpperCase();

const STATUS_BY_KEY = new Map(STATUS_CATALOG.map((status) => [statusKey(status.raw), status]));

export const findStatus = (raw) => STATUS_BY_KEY.get(statusKey(raw)) || null;

/** Texto legible de un estado. Si no está en el catálogo se muestra el valor original. */
export const statusLabel = (raw) => {
  if (!statusKey(raw)) return "Sin estado";
  return findStatus(raw)?.label || String(raw).trim();
};

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : "");

const titleCaseIfUpper = (text) =>
  text === text.toUpperCase() ? text.toLowerCase().replace(/(^|\s)\S/g, (char) => char.toUpperCase()) : text;

/**
 * "Camila - cobradora básica 0 a 60 días" → { name: "Camila", role: "Cobradora básica 0 a 60 días", isAi }.
 * Los agentes de IA se reconocen por el canal (LLAMADA_IA), no por el nombre.
 */
export const parseAdvisor = (advisorName, channel) => {
  const [name, ...roleParts] = String(advisorName || "").split(" - ");
  return {
    name: titleCaseIfUpper(name.trim()) || "Sin asesor",
    role: capitalize(roleParts.join(" - ").trim()),
    isAi: channel === "LLAMADA_IA",
  };
};

export const formatTime = (time) => (time ? String(time).slice(0, 5) : "");

const MIN_VALID_YEAR = 2000;

export const isValidManagementDate = (isoDate) => Number(String(isoDate || "").slice(0, 4)) >= MIN_VALID_YEAR;

/**
 * Agrupa el historial (ya ordenado del más reciente al más antiguo) por día. Dentro de cada día,
 * 2 o más INTENTO seguidos se colapsan en un bloque `attempts`; el resto queda como `single`.
 * Las gestiones con fecha inválida (vacía o anterior a 2000) van al grupo "invalid".
 */
export const groupHistoryByDay = (managements) => {
  const groups = [];
  managements.forEach((management) => {
    const dateKey = isValidManagementDate(management.managementDate)
      ? String(management.managementDate).slice(0, 10)
      : "invalid";
    let group = groups.find((item) => item.dateKey === dateKey);
    if (!group) {
      group = { dateKey, entries: [] };
      groups.push(group);
    }
    const last = group.entries[group.entries.length - 1];
    const isAttempt = management.result === "INTENTO";
    if (isAttempt && last?.type === "attempts") last.items.push(management);
    else if (isAttempt && last?.type === "single" && last.item.result === "INTENTO") {
      group.entries[group.entries.length - 1] = { type: "attempts", items: [last.item, management] };
    } else group.entries.push({ type: "single", item: management });
  });
  return groups;
};

/** Canales distintos dentro de un bloque de intentos, para el texto "· IA, asesor". */
export const attemptChannels = (items) => [...new Set(items.map((item) => CHANNELS[item.channel]?.label || CHANNELS.OTRO.label))];
